import React from 'react';
import { Button, Checkbox, Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import { ColumnShape } from 'react-base-table';

import {
  hasRelatedFlows,
  hasRelatedMetrics,
  isDeployed,
  isDraftVersion,
  isMultiVersion,
  isStoragePredefinedMappedEvent,
  isTraceId,
  isUndeployed,
} from '@src/emp-3.0/utils/checks';
import StatusTag from '@src/emp-3.0/components/common/StatusTag';
import { formatStatus } from '@src/emp-3.0/utils';
import {
  BLOCK_EVENTS,
  BLOCK_VIDEO_EVENTS,
  CUSTOM_DIMENSION,
  MAPPED_EVENTS,
  METRICS,
} from '@src/emp-3.0/constants/management';
import { getNameKeyByResource } from '@src/emp-3.0/utils/management';
import cloneLinedSvg from '@icons/emp/clone.svg';
import deployLinedSvg from '@icons/emp/deploy.svg';
import unDeployLineSvg from '@icons/emp/undeploy.svg';
import deleteLineSvg from '@icons/mapping-panel/delete-icon.svg';

import {
  ActionsCellRendererWrapper,
  CommonCellRendererWrapper,
  ListCellRendererWrapper,
  ListItem,
  ListItemText,
  NameCellRendererWrapper,
  NameTextContainer,
} from './styled';
import {
  ActionConfirmButton,
  getRelatedConfirmTitle,
  renderDeployedStatusDeletePrompt,
  renderDeployPrompt,
  renderUndeployPrompt,
} from './ActionConfirmButton';
import { ICommonCellRendererProps } from './typed';
import {
  workbenchModeTrueAndWorkbenchAsset,
  workbenchModeTrueButNotWorkbenchAsset,
} from '@src/emp-3.0/components/WorkbenchButton/util';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

/**
 * check whether mapped_event/dimension is predefined by rowData and column definition
 */
function checkPredefine(props: Partial<ICommonCellRendererProps>) {
  const { rowData, column = {} } = props;
  const isPredefined =
    (column.type === MAPPED_EVENTS && isStoragePredefinedMappedEvent(rowData)) ||
    (column.type === CUSTOM_DIMENSION && rowData.is_predefined);
  return isPredefined;
}

export const CommonCellRenderer: React.FC<Partial<ICommonCellRendererProps>> = (props) => {
  const { cellData } = props;
  return <CommonCellRendererWrapper>{cellData}</CommonCellRendererWrapper>;
};

export const StatusCellRenderer: React.FC<Partial<ICommonCellRendererProps>> = (props) => {
  const { cellData } = props;
  let label: string | null | JSX.Element = formatStatus(cellData);
  if (isDeployed({ status: cellData })) {
    label = (
      <StatusTag type="primary" typeColor="green">
        {label}
      </StatusTag>
    );
  } else if (isUndeployed({ status: cellData })) {
    label = (
      <StatusTag type="primary" typeColor="orange" size="small">
        {label}
      </StatusTag>
    );
  }
  return <CommonCellRendererWrapper>{label}</CommonCellRendererWrapper>;
};

export const CheckboxCellRenderer: React.FC<ICommonCellRendererProps> = (props) => {
  const { column, rowData } = props;
  const { selectedList = [], onChange, disable } = column ?? {};

  const isPredefined = checkPredefine(props);

  const checked = selectedList?.find((item) => item.id === rowData.id);
  const handleCheckboxChange = (e) => {
    const newList = e.target.checked
      ? [...selectedList, rowData]
      : selectedList.filter((item) => item.id !== rowData.id);

    onChange?.({ selectedList: [...newList] });
  };
  if (isPredefined) return <div />;
  return (
    <Checkbox
      checked={checked}
      onChange={handleCheckboxChange}
      style={{ alignSelf: 'self-start', padding: '12px 0' }}
      disabled={disable?.(rowData)}
    />
  );
};

export const RowNumberCellRenderer: React.FC<ICommonCellRendererProps> = (props) => {
  const { cellData, rowData, rowIndex } = props;
  const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
  const content = isSubVersion ? '' : (cellData ?? rowIndex) + 1;

  return <CommonCellRendererWrapper>{content}</CommonCellRendererWrapper>;
};

export const NameCellRenderer: React.FC<Partial<ICommonCellRendererProps>> = (props) => {
  const { cellData, rowData, column } = props;
  const {
    onClick,
    selectedList = [],
    showDraftPrefix,
    style,
    disable,
  } = (column ?? {}) as Partial<ColumnShape<any>>;
  const isSelected = selectedList?.includes(cellData);
  const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
  const isPredefined = checkPredefine(props);
  const disabledCell = isPredefined || disable?.(rowData);

  const handleClick = () => {
    !disabledCell && onClick?.({ cellData, rowData });
  };

  return (
    <NameCellRendererWrapper style={style}>
      <NameTextContainer
        showDraft={showDraftPrefix && isSubVersion}
        disabledCell={disabledCell}
        onClick={handleClick}
        style={
          isSelected
            ? {
                fontWeight: 600,
              }
            : {}
        }
      >
        {cellData}
      </NameTextContainer>
      {isPredefined ? (
        <Tooltip title="Conviva predefined">
          <StatusTag type="ghost" typeColor="gray" style={{ height: '22px' }}>
            Conviva
          </StatusTag>
        </Tooltip>
      ) : null}
      {isSubVersion ? (
        <StatusTag
          type="primary"
          typeColor="teal"
          style={{ height: '22px', alignSelf: 'self-start' }}
        >
          Draft
        </StatusTag>
      ) : null}
      {workbenchModeTrueAndWorkbenchAsset(rowData?.is_beta) && (
        <StatusTag
          type="ghost"
          typeColor="teal"
          style={{
            height: '20px',
            paddingInline: '7px',
          }}
        >
          Workbench
        </StatusTag>
      )}
    </NameCellRendererWrapper>
  );
};

export const ListCellRenderer: React.FC<ICommonCellRendererProps> = (props) => {
  const { cellData, column, rowData } = props;
  const { placeholder = '--', dataKey, onClick, getPlaceholder } = column ?? {};

  const data = Array.isArray(cellData) ? cellData : [];

  const { expandedState } = rowData;
  const isShowMore = dataKey && expandedState?.[dataKey];

  const showButton = data.length > 5 && dataKey && expandedState?.[dataKey] !== undefined;
  const buttonText = isShowMore ? 'Show more...' : 'Show less...';
  const displayedData = data.length <= 5 ? data : isShowMore ? data.slice(0, 5) : data;

  const computedPlaceholder = getPlaceholder ? getPlaceholder({ rowData }) : placeholder;

  return (
    <ListCellRendererWrapper>
      {data.length === 0
        ? computedPlaceholder
        : displayedData.map((item, index) => (
            <ListItem key={`list_item_cell_renderer_${index}`}>
              <ListItemText>{item}</ListItemText>
            </ListItem>
          ))}
      {showButton && (
        <div
          style={{ color: '#0046FF', cursor: 'pointer' }}
          onClick={() => {
            if (dataKey && expandedState) {
              onClick?.({
                rowData: {
                  ...rowData,
                  expandedState: { ...expandedState, [dataKey]: !isShowMore },
                },
              });
            }
          }}
        >
          {buttonText}
        </div>
      )}
    </ListCellRendererWrapper>
  );
};

export const ActionsCellRenderer: React.FC<ICommonCellRendererProps> = (props) => {
  const { rowData, column } = props;
  const {
    type, // metrics | flow | custom_dimension | mapped_events | block_events | block_video_events
    cloneVisible, // boolean
    onClone,
    deployVisible, // boolean
    onDeploy,
    undeployVisible, // boolean
    onUndeploy,
    deleteVisible, // boolean
    onDelete,
    unblockVisible, // boolean
    unblockConfirmTitle, // React.ReactNode
    onUnblock,
  } = column ?? {};

  const nameKey = getNameKeyByResource(type);
  const name = rowData[nameKey];
  const { workbenchMode } = useInitialInfoStore.getState();
  // common
  const isDeploy = isDeployed(rowData);
  const isMainVersion = isMultiVersion(rowData) && !isDraftVersion(rowData);
  // dimension
  const isTraceIdDimension = type === CUSTOM_DIMENSION && isTraceId(rowData);
  // mapped event
  const isHasRelatedMetrics = [MAPPED_EVENTS, METRICS].includes(type)  && hasRelatedMetrics(rowData);
  const isHasRelatedFlows = type === MAPPED_EVENTS && hasRelatedFlows(rowData);
  const hasRelated = isHasRelatedMetrics || isHasRelatedFlows;
  const isPredefined = checkPredefine(props);

  const handleClone = () => {
    onClone?.({ rowData });
  };

  const handleDeploy = () => {
    onDeploy?.({ rowData });
  };

  const handleUndeploy = () => {
    onUndeploy?.({ rowData });
  };

  const handleDelete = () => {
    onDelete?.({ rowData });
  };

  const handleUnblock = () => {
    onUnblock?.({ rowData });
  };

  const getUnblockConfirmTitle = () => {
    if (unblockConfirmTitle) return unblockConfirmTitle;
    switch (type) {
      case BLOCK_EVENTS:
      case BLOCK_VIDEO_EVENTS:
        return (
          <>
            <div>Unblock this item? Once unblocked, it will be removed from</div>
            <div>the Blocked Events and sensor data reporting will resume.</div>
          </>
        );
      default:
        return 'Unblock this item?';
    }
  };

  const cloneButton = (
    <Tooltip key="management_clone_button" title={'Clone'}>
      <Button
        type="link"
        icon={<Icon component={cloneLinedSvg} style={{ color: '#0046FF' }} />}
        onClick={handleClone}
      />
    </Tooltip>
  );

  const deployButton = (
    <ActionConfirmButton
      key="management_deploy_button"
      text="Deploy"
      canAction
      onConfirm={handleDeploy}
      icon={<Icon component={deployLinedSvg} style={{ color: 'rgba(0, 0, 0, 0.88)' }} />}
      title={`Deploy ${name}?`}
      description={<div style={{ margin: '8px 22px 0 0' }}>{renderDeployPrompt()}</div>}
      tooltip="Deploy to Pulse"
      style={{ color: '#0046FF' }}
      okButtonProps={{}}
    />
  );

  const undeployButtonProps = {
    key: 'management_undeploy_button',
    text: 'Undeploy',
    canAction: !hasRelated,
    onConfirm: handleUndeploy,
    title: !hasRelated ? `Undeploy ${name}?` : getRelatedConfirmTitle(rowData, 'undeploy'),
    style: { color: '#F5222D' },
  };
  if (!hasRelated) {
    Object.assign(undeployButtonProps, {
      icon: <Icon component={unDeployLineSvg} style={{ color: 'rgba(0, 0, 0, 0.88)' }} />,
      description: <div style={{ margin: '8px 22px 0 0' }}>{renderUndeployPrompt()}</div>,
      tooltip: 'Undeploy from Pulse',
      okButtonProps: { danger: true },
    });
  }
  const undeployButton = <ActionConfirmButton {...undeployButtonProps} />;
  const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
  const canDeleted = isSubVersion || !hasRelated;

  const deleteButtonProps = {
    key: 'management_delete_button',
    text: 'Delete',
    canAction: canDeleted,
    onConfirm: handleDelete,
    title: canDeleted ? `Delete ${name}?` : getRelatedConfirmTitle(rowData, 'delete'),
    style: { color: '#F5222D' },
  };
  if (canDeleted) {
    Object.assign(deleteButtonProps, {
      icon: <Icon component={deleteLineSvg} style={{ color: 'rgba(0, 0, 0, 0.88)' }} />,
      description: (
        <div style={{ margin: '8px 22px 0 0' }}>{renderDeployedStatusDeletePrompt()}</div>
      ),
      okText: isDeploy && !workbenchMode ? 'Undeploy & Delete' : 'Delete',
      okButtonProps: { danger: true },
    });
  }
  const deleteButton = <ActionConfirmButton {...deleteButtonProps} />;

  const unblockButton = (
    <ActionConfirmButton
      key="management_unblock_button"
      text="Unblock"
      canAction
      onConfirm={handleUnblock}
      title={getUnblockConfirmTitle()}
    />
  );

  const renderButtons = () => {
    if (isPredefined || workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta)) return null;

    if (isTraceIdDimension) return isDeploy ? null : deployButton;

    const buttons: JSX.Element[] = [];

    if (cloneVisible && !isMainVersion) {
      buttons.push(cloneButton);
    }

    if (isDeploy && !workbenchModeTrueAndWorkbenchAsset(rowData?.is_beta)) {
      if (undeployVisible) buttons.push(undeployButton);
    } else if (deployVisible && !workbenchModeTrueAndWorkbenchAsset(rowData?.is_beta)) {
      buttons.push(deployButton);
    }

    if (deleteVisible) {
      buttons.push(deleteButton);
    }

    if (unblockVisible) {
      buttons.push(unblockButton);
    }

    return <>{buttons}</>;
  };

  return <ActionsCellRendererWrapper>{renderButtons()}</ActionsCellRendererWrapper>;
};
